import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { uiSettingsSchema } from '../../../../shared/source/data-contract/user-ui-settings';

import type { SerializedUiSettings, UserUiSettings } from '../../../../shared/source/data-contract/user-ui-settings';
import type { ValidatingHttpFetcherWithBaseUrl } from '../../../../shared/source/validating-http-fetcher';
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import type { QueryKey, UseQueryResult } from '@tanstack/react-query';

const pathname = `/users/me/settings/ui`;
const queryKey: QueryKey = [pathname, 'get'];

const serializeSetting = <K extends keyof UserUiSettings>(value: any | null): SerializedUiSettings[K] =>
    value === null ? null : typeof value === 'object' ? JSON.stringify(value) : value;

const mapObject = <V, M, R extends Record<string, V>>(object: R, mapper: (value: V) => M): Record<string, M> =>
    Object.fromEntries(Object.entries(object).map(([key, value]) => [key, mapper(value)]));

const serializeSettings = (settings: UserUiSettings): SerializedUiSettings => mapObject(settings, serializeSetting);

export const useUserSettingsUi = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
    options: { readonly enabled: boolean },
): UseQueryResult<UserUiSettings> =>
    useQuery({
        enabled: options.enabled,
        queryFn: async () => {
            const result = await validatingCobrainerApiFetcher.get(uiSettingsSchema, {
                pathname,
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        queryKey,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
    });

export const useUserSettingsUiMutation = (
    validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl,
): {
    readonly userSettingsUiMutation: UseMutationResult<UserUiSettings, Error, Partial<UserUiSettings>>;
    readonly userSettingsUiMutationOptimistic: UseMutationResult<UserUiSettings, Error, Partial<UserUiSettings>>;
} => {
    const queryClient = useQueryClient();

    const optimisticMutation: UseMutationOptions<UserUiSettings, Error, Partial<UserUiSettings>> = {
        mutationFn: async (data) => {
            const result = await validatingCobrainerApiFetcher.patchJson(uiSettingsSchema, {
                pathname,
                payload: serializeSettings(data),
            });

            if (result.isOk) {
                return result.value;
            }

            throw result.error;
        },
        onMutate: async (newSettings) => {
            await queryClient.cancelQueries({ queryKey });
            queryClient.setQueryData(queryKey, (oldSettings: UserUiSettings) => ({
                ...oldSettings,
                ...newSettings,
            }));
        },
        onSuccess: (settings) => {
            queryClient.setQueryData(queryKey, settings);
        },
    };

    const userSettingsUiMutation = useMutation({
        ...optimisticMutation,
        onError: () => queryClient.invalidateQueries({ queryKey }),
    });

    const userSettingsUiMutationOptimistic = useMutation(optimisticMutation);

    return { userSettingsUiMutation, userSettingsUiMutationOptimistic };
};
